import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Layout from "../../../components/layout/Layout";
import {
    useGetActiveTiersQuery,
    useGetAccountFilterQuery,
    useGetUserListQuery,
} from "../../../redux/service/modules/sales";
import CommonNavigator from "../../../components/common/CommonNavigator";
import { CustomAdminLayout } from "../../../components/layout/CustomAdminLayout";
import CustomDrawer from "../../../components/layout/CustomDrawer";
import FilterPanel from "../../../components/filters/FilterPanel";
import PaginatedDataTable from "../../../components/table/PaginatedDataTable";
import { useGetClientAccountsQuery } from "../../../redux/service/modules/sales";
import { useLazyGetLazyClientAccountsQuery } from "../../../redux/service/modules/sales/lazzyQuery";
import CustomToggle from "../../../components/common/CustomToggle";
import { debounce } from "../../../utils/commonUtils";
import { handleExport } from "../../../redux/service/modules/exportToExcel";
import { convertToDates } from "../../../utils/commonUtils";
import CustomCurrencyFormat from "../../../components/common/CurrencyFormatter";

const Accounts = () => {
    const [isFilterOpen, toggleFilter] = useState(false);
    const [availableColumns, setAvailableColumns] = useState([]);
    const dateRange = useSelector((state) => state.date);
    const [requiredFilters, setRequiredFilters] = useState([
        "geography",
        "category",
        "sub_category",
        "primary_sales",
        "secondary_sales",
        "trader",
        "secondary_trader",
        "service_intensity",
        "aum",
        "client_volume",
        "ambit_volume",
        "revenue",
        "revenue_till_date",
        "final_budget",
        "india_holding",
    ]);

    const columnFilter = useSelector((state) => state.columnFilter);
    const [filterDetails, setFilterDetails] = useState({
        appliedFilters: {
            search: "",
            tier: "",
            initialDateRange: dateRange,
            financial_year: new Date().getFullYear(),
        },
        filterCount: 0,
        rawFilters: {},
    });
    const [filters, setFilters] = useState([]);

    const { data: clientFilters } = useGetAccountFilterQuery();
    const [accountsExcelQuery] = useLazyGetLazyClientAccountsQuery();

    const { data: userList } = useGetUserListQuery();

    const availableFiltersMap = {
        geography: {
            name: "geography",
            label: "Location",
            key: "geography",
            options: clientFilters?.client_geography?.map((location) => ({
                label: location,
                id: location,
            })),
            multiSelect: true,
        },
        category: {
            name: "category",
            label: "Category",
            key: "category",
            options: clientFilters?.categories
                ?.map((category) => ({
                    label: category.category_name,
                    id: category.id,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
            multiSelect: true,
        },
        sub_category: {
            name: "sub_category",
            label: "Sub Category",
            key: "sub_category",
            options: clientFilters?.subcategories
                ?.map((sub_category) => ({
                    label: `${sub_category.category_name} - ${sub_category.sub_category_name}`,
                    id: sub_category.id,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
            multiSelect: true,
        },
        primary_sales: {
            name: "primary_sales",
            label: "Primary RM",
            key: "primary_sales",
            options:
                userList
                    ?.filter(
                        (user) =>
                            user.role === "sales" ||
                            user.role === "head_of_sales"
                    )
                    .map((user) => ({
                        label: user.username,
                        id: user.id,
                    })) || [],
            multiSelect: true,
        },
        secondary_sales: {
            name: "secondary_sales",
            label: "Secondary RM",
            key: "secondary_sales",
            options:
                userList
                    ?.filter(
                        (user) =>
                            user.role === "sales" ||
                            user.role === "head_of_sales"
                    )
                    .map((user) => ({
                        label: user.username,
                        id: user.id,
                    })) || [],
            multiSelect: true,
        },
        trader: {
            name: "trader",
            label: "Trader",
            key: "trader",
            options:
                userList
                    ?.filter((user) => user.role === "trader")
                    .map((user) => ({
                        label: user.username,
                        id: user.id,
                    })) || [],
            multiSelect: true,
        },
        secondary_trader: {
            name: "secondary_trader",
            label: "Secondary Trader",
            key: "secondary_trader",
            options:
                userList
                    ?.filter((user) => user.role === "trader")
                    .map((user) => ({
                        label: user.username,
                        id: user.id,
                    })) || [],
            multiSelect: true,
        },

        service_intensity: {
            name: "service_intensity",
            label: "Service Intensity Cost",
            key: "service_intensity",
            options: [
                { name: "0 to 100k", value: "0-100000" },
                { name: "100k - 1m", value: "100001-1000000" },
                { name: "1m - 50m", value: "1000001-50000000" },
                { name: "50m - 100m", value: "50000001-100000000" },
                { name: "> 100m", value: "100000001-" },
            ].map((intensity) => ({
                label: intensity.name,
                id: intensity.value,
            })),
            multiSelect: false,
        },
        aum: {
            name: "aum",
            label: "AUM",
            key: "aum",
            min: 0,
            max: 1000,
            range: [10, 800],
            type: "range-slider",
        },
        india_holding: {
            name: "india_holding",
            label: "India Holding",
            key: "india_holding",
            min: 0,
            max: 1000,
            range: [10, 800],
            type: "range-slider",
        },
        client_volume: {
            name: "client_volume",
            label: "Client Volume",
            key: "client_volume",
            min: 0,
            max: 1000,
            range: [10, 800],
            type: "range-slider",
        },
        ambit_volume: {
            name: "ambit_volume",
            label: "Ambit Volume",
            key: "ambit_volume",
            min: 0,
            max: 1000,
            range: [10, 800],
            type: "range-slider",
        },
        final_budget: {
            name: "final_budget",
            label: "Budget CY",
            key: "final_budget",
            min: 0,
            max: 1000,
            range: [10, 800],
            type: "range-slider",
        },
        revenue_till_date: {
            name: "revenue_till_date",
            label: "Revenue YTD",
            key: "revenue_till_date",
            min: 0,
            max: 1000,
            range: [10, 800],
            type: "range-slider",
        },
        revenue: {
            name: "revenue",
            label: "Revenue CY",
            key: "revenue",
            min: 0,
            max: 1000,
            range: [10, 800],
            type: "range-slider",
        },
    };

    useEffect(() => {
        updateFiltersBasedOnRequired();
    }, [requiredFilters, clientFilters, columnFilter]);

    const updateFiltersBasedOnRequired = () => {
        const activeFilters = requiredFilters
            .map((filterKey) => availableFiltersMap[filterKey])
            .filter((filter) => !columnFilter.includes(filter.key));

        setFilters(activeFilters);
    };

    const onFilterApply = (transformedFilters, filterCount, rawFilters) => {
        toggleFilter(false);
        setFilterDetails({
            ...filterDetails,
            appliedFilters: {
                ...transformedFilters,
                financial_year: filterDetails.appliedFilters.financial_year,
            },
            filterCount,
            rawFilters,
        });
    };

    const handleSearch = debounce((value) => {
        setFilterDetails({
            ...filterDetails,
            appliedFilters: {
                ...filterDetails.appliedFilters,
                search: value,
            },
        });
    }, 500);

    const SwitchClientToCorporateToggle = React.memo(() => {
        const onChange = useCallback(
            (val) => {
                setFilterDetails((prevDetails) => ({
                    ...prevDetails,
                    appliedFilters: {
                        ...prevDetails.appliedFilters,
                        isClient: val,
                    },
                }));
            },
            [setFilterDetails]
        );
        const options = ["Client", "Corporate"];
        return (
            <CustomToggle
                options={options}
                onChange={onChange}
                selectedValue={
                    filterDetails?.appliedFilters?.isClient || options[0]
                }
            />
        );
    });

    const triggerExportToExcel = () => {
        handleExport(accountsExcelQuery, {
            appliedFilters: filterDetails.appliedFilters,
            ...(dateRange) || {},
        });
    };
    const fieldsToRender = [
        "revenue_till_date",
        "aum",
        "client_volume",
        "ambit_volume",
        "final_budget",
        "revenue",
        "service_intensity",
        "revenue_till_date",
        "india_holding",
        "bps",
    ];
    const customCellRender = fieldsToRender.reduce((renderers, field) => {
        renderers[field] = (row) => <CustomCurrencyFormat value={row[field]} />;
        return renderers;
    }, {});

    const handleTierChange = (selectedOptions) => {
        setFilterDetails({
            ...filterDetails,
            appliedFilters: {
                ...filterDetails.appliedFilters,
                tier: selectedOptions.map((option) => option.id).join(","),
            },
        });
    };
    const handleYearChange = (year) => {
        setFilterDetails({
            ...filterDetails,
            appliedFilters: {
                ...filterDetails.appliedFilters,
                "financial_year": year,
            },
        });
    };
    return (
        <Layout onYearPickerChange={handleYearChange}>
            <CommonNavigator
                title={"Accounts"}
                showExportToExcel={true}
                triggerExportToExcel={triggerExportToExcel}
            />
            <div className="mt-1">
                <CustomAdminLayout
                    title={""}
                    showFilter
                    filterCount={filterDetails.filterCount}
                    onFilterClick={() => toggleFilter(true)}
                    showSearchInput
                    onSearch={handleSearch}
                    searchPlaceholder={"Search Client"}
                    isNotAdmin
                >
                    <PaginatedDataTable
                        queryHook={useGetClientAccountsQuery}
                        paginationEnabled={true}
                        appliedFilters={filterDetails.appliedFilters}
                        showColumnSelector
                        stickyColumnsCount={3}
                        isColumnSticky={true}
                        showToggleSwitchElm={false}
                        toggleSwitchElm={<SwitchClientToCorporateToggle />}
                        showTierSelector
                        isClient={true}
                        customCellRender={customCellRender}
                        setAvailableColumns={setAvailableColumns}
                        tierChange={handleTierChange}
                        defaultPageSize={50}
                    />
                </CustomAdminLayout>
                <CustomDrawer open={isFilterOpen}>
                    <FilterPanel
                        filtersContent={filters}
                        handlePanelClose={() => toggleFilter(false)}
                        onApply={onFilterApply}
                        appliedFilters={filterDetails.rawFilters}
                    />
                </CustomDrawer>
            </div>
        </Layout>
    );
};

export default Accounts;
